import {
  Component,
  computed,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  MatFormField,
  MatLabel,
  MatOption,
  MatSelect,
  MatSelectChange,
} from "@angular/material/select";
import { Router } from "@angular/router";
import { TranslatePipe } from "@ngx-translate/core";
import { lastValueFrom, map } from "rxjs";
import { CompetitorSelectionDialog } from "../../../shared/components/competitor-selection-dialog/competitor-selection-dialog";
import { MainModalDialog } from "../../../shared/components/main-modal-dialog/main-modal-dialog";
import {
  pathRouteDataHelper$,
  pathRouteParametersHelper$,
} from "../../../shared/helpers/path-params.helper";
import {
  DefaultComparison,
  DefaultProduct,
} from "../../../shared/pia-api/generated";
import { PiaCompetitorsService } from "../../../shared/pia-api/pia-competitors.service";

@Component({
  selector: "claas-product-compare-select",
  imports: [
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    TranslatePipe,
    MainModalDialog,
    CompetitorSelectionDialog,
  ],
  templateUrl: "./product-compare-select.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./product-compare-select.scss",
})
export class ProductCompareSelect {
  private router = inject(Router);
  private piaCompetitorsService = inject(PiaCompetitorsService);
  data = toSignal(pathRouteDataHelper$());
  params = toSignal(pathRouteParametersHelper$());
  showIncomparableModal = signal(false);

  competitorID = toSignal(
    pathRouteParametersHelper$().pipe(
      map((params) => params["competitorId"] || ""),
    ),
    {
      initialValue: "",
    },
  );

  userSelectedProduct = signal("");

  productDescendants = computed((): DefaultProduct[] => {
    return this.data()?.["productDescendants"] ?? [];
  });

  selectedProductDescendant = computed((): DefaultProduct | null => {
    // use product id path parameter to get the selected product
    const productId = this.userSelectedProduct()
      ? this.userSelectedProduct()
      : this.params()?.["productId"];

    const selectedProduct =
      this.data()?.["productDescendants"]?.find(
        (productDescendant: DefaultProduct) =>
          productDescendant.ID === productId,
      ) ?? this.data()?.["productDescendants"]?.[0];

    if (selectedProduct) {
      return selectedProduct;
    }

    return null;
  });

  constructor() {
    effect(async () => {
      await this.showIncomparableModalOrNavigate(this.userSelectedProduct());
    });
  }

  async showIncomparableModalOrNavigate(selectedProduct: string) {
    const currentParams = this.params();

    const comparableCompetitorIds = (
      await lastValueFrom(
        this.piaCompetitorsService.getComparisonByContextAndProductId({
          countryID: currentParams?.["country"]?.toUpperCase(),
          languageID: currentParams?.["language"],
          year: currentParams?.["year"],
          productID: selectedProduct
            ? selectedProduct
            : currentParams?.["productId"],
        }),
      )
    )?.map((comparison: DefaultComparison) => comparison.CompetitorId);

    if (
      !comparableCompetitorIds?.length ||
      !currentParams?.["competitorId"] ||
      !comparableCompetitorIds?.includes(currentParams?.["competitorId"])
    ) {
      this.showIncomparableModal.set(true);
    } else if (selectedProduct) {
      // Navigate to the same route structure but with the new productId
      await this.router.navigate([
        "/pia",
        currentParams?.["country"],
        currentParams?.["language"],
        currentParams?.["year"],
        "product",
        currentParams?.["id"],
        this.router.url.includes("arguments") ? "arguments" : "tech-data",
        this.userSelectedProduct(),
        "comparison",
        currentParams?.["competitorId"],
      ]);
    }
  }

  async handleCompetitorSelectValueChange(value?: string | null) {
    this.showIncomparableModal.set(false);
    const currentParams = this.params();

    if (!value) {
      this.userSelectedProduct.set(currentParams?.["productId"]);
    }

    await this.router.navigate([
      "/pia",
      currentParams?.["country"],
      currentParams?.["language"],
      currentParams?.["year"],
      "product",
      currentParams?.["id"],
      this.router.url.includes("arguments") ? "arguments" : "tech-data",
      this.userSelectedProduct()
        ? this.userSelectedProduct()
        : currentParams?.["productId"],
      "comparison",
      value ? value : currentParams?.["competitorId"],
    ]);
  }

  handleProductSelectChange(evt: MatSelectChange) {
    this.userSelectedProduct.set(evt.value);
  }
}
